import { useMemo, useState } from 'react';
import { Modal } from '@/deploy/components/ui/Modal';
import { Button } from '@/deploy/components/ui/Button';
import { Toggle } from '@/deploy/components/ui/Toggle';
import { Icon } from '@/deploy/components/ui/Icon';
import { networkService } from '@/deploy/services/network.service';
import type { Certificate, NetworkJob, ProjectDomains, Vhost, VhostPayload } from '@/deploy/types/network';
import { errorMessage } from './networkFormat';
import { AccessRulesEditor, toDrafts, toPayload, type RuleDraft } from './AccessRulesEditor';
import styles from './Network.module.scss';

type Mode = 'create' | 'edit' | 'adopt';

interface VhostFormModalProps {
  projectId: string;
  mode: Mode;
  domains: ProjectDomains;
  /** Certificats internes du projet : un wildcard peut servir plusieurs vhosts. */
  certificates: Certificate[];
  vhost?: Vhost;
  onClose: () => void;
  onJob: (job: NetworkJob, title: string) => void;
}

const TITLES: Record<Mode, string> = {
  create: 'Nouveau domaine',
  edit: 'Modifier le domaine',
  adopt: 'Reprendre le vhost',
};

/** Un certificat couvre le domaine s'il le nomme, ou par un wildcard d'un seul niveau. */
function covers(cert: Certificate, domain: string): boolean {
  return cert.domains.some((d) => {
    if (d === domain) return true;
    if (!d.startsWith('*.')) return false;
    const suffix = d.slice(1);
    const label = domain.endsWith(suffix) ? domain.slice(0, domain.length - suffix.length) : '';
    return label !== '' && !label.includes('.');
  });
}

export function VhostFormModal({ projectId, mode, domains, certificates, vhost, onClose, onJob }: VhostFormModalProps) {
  const [sub, setSub] = useState('');
  const [main, setMain] = useState(domains.domains[0]?.name ?? '');
  const [upstreams, setUpstreams] = useState<string[]>(vhost?.upstreams.length ? vhost.upstreams : ['http://']);
  const [websocket, setWebsocket] = useState(vhost?.websocket ?? false);
  const [bodySize, setBodySize] = useState(vhost?.clientMaxBodySize ?? '');
  const [htpasswd, setHtpasswd] = useState(vhost?.htpasswdFile ?? '');
  const [rules, setRules] = useState<RuleDraft[]>(() => toDrafts(vhost?.accessRules));
  const [extraServer, setExtraServer] = useState(vhost?.extraServerConfig ?? '');
  const [extraLocation, setExtraLocation] = useState(vhost?.extraLocationConfig ?? '');
  const [createDns, setCreateDns] = useState(true);
  const [proxied, setProxied] = useState(false);
  const [certificateId, setCertificateId] = useState<number | null>(vhost?.certificateId ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const domain = mode === 'create'
    ? (sub.trim() ? `${sub.trim().toLowerCase()}.${main}` : '')
    : vhost!.domain;
  const mainInfo = domains.domains.find((d) => d.name === main);
  const usableCerts = useMemo(() => certificates.filter((c) => c.kind === 'INTERNAL' && domain && covers(c, domain)),
    [certificates, domain]);

  const payload = (): VhostPayload => ({
    domain,
    upstreams: upstreams.map((u) => u.trim()).filter(Boolean),
    websocket,
    clientMaxBodySize: bodySize.trim() || null,
    htpasswdFile: htpasswd.trim() || null,
    extraServerConfig: extraServer.trim() || null,
    extraLocationConfig: extraLocation.trim() || null,
    ...(mode === 'create' ? { createDns: createDns && !!mainInfo?.zone, proxied } : {}),
    certificateId,
    accessRules: toPayload(rules),
  });

  const emptyRule = rules.some((r) => r.ips.length === 0 && r.users.length === 0 && r.keys.length === 0);
  const ipBehindCloudflare = mode === 'create' && createDns && proxied && rules.some((r) => r.ips.length > 0);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const showPreview = () => run(async () => setPreview(await networkService.previewVhost(projectId, payload())));

  const submit = () => run(async () => {
    if (mode === 'create' && !sub.trim()) throw new Error('Le sous-domaine est requis');
    const job = mode === 'create'
      ? await networkService.createVhost(projectId, payload())
      : mode === 'edit'
        ? await networkService.updateVhost(projectId, domain, payload())
        : await networkService.adoptVhost(projectId, domain, payload());
    onJob(job, `${TITLES[mode]} : ${domain}`);
  });

  const setUpstream = (i: number, value: string) => setUpstreams((prev) => prev.map((u, j) => (j === i ? value : u)));

  return (
    <Modal
      open
      onClose={onClose}
      title={TITLES[mode]}
      subtitle={mode === 'create' ? undefined : domain}
      width="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="secondary" onClick={showPreview} disabled={busy || !domain} leftIcon={<Icon name="eye" size={14} />}>
          Aperçu
        </Button>
        <Button variant="primary" onClick={submit} loading={busy} disabled={busy || !domain || emptyRule}>
          {mode === 'create' ? 'Créer' : mode === 'edit' ? 'Enregistrer' : 'Reprendre'}
        </Button>
      </>}
    >
      <div className={styles.form}>
        {mode === 'adopt' && (
          <div className={styles.info}>
            Ce vhost a été importé tel quel. Le reprendre régénère son fichier depuis le modèle commun :
            vérifie l'aperçu avant, les réglages faits à la main non repris ici seront perdus.
          </div>
        )}

        {mode === 'create' && (
          <label className={styles.field}>
            <span>Domaine</span>
            <div className={styles.domainInput}>
              <input value={sub} onChange={(e) => setSub(e.target.value)} placeholder="api" autoFocus />
              <span className={styles.dot}>.</span>
              <select value={main} onChange={(e) => setMain(e.target.value)}>
                {domains.domains.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            {mainInfo && !mainInfo.zone && (
              <span className={styles.hint}>Aucune zone Cloudflare ne porte {main} : le DNS devra être créé à la main.</span>
            )}
          </label>
        )}

        <div className={styles.field}>
          <span>Upstreams</span>
          <div className={styles.inlineList}>
            {upstreams.map((u, i) => (
              <div key={i} className={styles.inlineItem}>
                <input value={u} onChange={(e) => setUpstream(i, e.target.value)} placeholder="http://10.0.0.12:8080" />
                {upstreams.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setUpstreams((prev) => prev.filter((_, j) => j !== i))}
                    leftIcon={<Icon name="x" size={12} />} aria-label="Retirer" />
                )}
              </div>
            ))}
          </div>
          <div>
            <Button variant="ghost" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={() => setUpstreams((prev) => [...prev, 'http://'])}>
              Ajouter un upstream
            </Button>
          </div>
          <span className={styles.hint}>Plusieurs upstreams : répartition de charge entre eux (même schéma, sans chemin).</span>
        </div>

        <div className={styles.formRow}>
          <label className={styles.field}>
            <span>Taille max du corps</span>
            <input value={bodySize} onChange={(e) => setBodySize(e.target.value)} placeholder="1m (défaut nginx)" />
          </label>
          <label className={styles.field}>
            <span>Certificat</span>
            <select value={certificateId ?? ''} onChange={(e) => setCertificateId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">{mode === 'create' ? 'Nouveau certificat (automatique)' : 'Certificat actuel'}</option>
              {usableCerts.filter((c) => c.id !== vhost?.certificateId).map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.domains.join(', ')})</option>
              ))}
            </select>
          </label>
        </div>

        <Toggle checked={websocket} onChange={setWebsocket} label="WebSocket"
          description="Ajoute les en-têtes Upgrade / Connection et un timeout de lecture d'une heure." />

        {mode === 'create' && (
          <>
            <Toggle checked={createDns && !!mainInfo?.zone} onChange={setCreateDns} disabled={!mainInfo?.zone}
              label="Créer le DNS"
              description={`CNAME ${domain || '<domaine>'} → ${domains.cnameTarget ?? 'reverse proxy'}${mainInfo?.account ? ` (compte ${mainInfo.account})` : ''}`} />
            {createDns && mainInfo?.zone && (
              <Toggle checked={proxied} onChange={setProxied} label="Proxy Cloudflare"
                description="Nuage orange. Laisser désactivé sauf besoin précis : le trafic passe alors par Cloudflare." />
            )}
          </>
        )}

        <AccessRulesEditor rules={rules} onChange={setRules} />
        {ipBehindCloudflare && (
          <div className={styles.error}>
            Proxy Cloudflare activé : nginx verra les IP de Cloudflare et non celles des clients, la whitelist IP ne filtrera rien d'utile.
          </div>
        )}
        {(mode === 'adopt' || htpasswd) && (
          <label className={styles.field}>
            <span>Importer un fichier htpasswd existant</span>
            <input value={htpasswd} onChange={(e) => setHtpasswd(e.target.value)} placeholder="nom du fichier dans /shared/nginx/htpasswd" />
            <span className={styles.hint}>
              Ses utilisateurs sont ajoutés à la règle « Tout le domaine » (créée si besoin), avec leurs mots de passe actuels.
              Le fichier lui-même n'est pas modifié.
            </span>
          </label>
        )}

        <details className={styles.details}>
          <summary>Options avancées</summary>
          <div className={styles.form}>
            <label className={styles.field}>
              <span>Config du bloc server</span>
              <textarea value={extraServer} onChange={(e) => setExtraServer(e.target.value)} placeholder="add_header X-Frame-Options DENY;" />
            </label>
            <label className={styles.field}>
              <span>Config des locations (/ et chemins protégés)</span>
              <textarea value={extraLocation} onChange={(e) => setExtraLocation(e.target.value)} placeholder="proxy_buffering off;" />
            </label>
            <span className={styles.hint}>Injecté tel quel, validé par nginx -t avant tout reload.</span>
          </div>
        </details>

        {error && <div className={styles.error}>{error}</div>}
        {preview !== null && <pre className={styles.code}>{preview}</pre>}
      </div>
    </Modal>
  );
}
